import { Alert, Button, Divider, Group, List, Paper, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { receiptApi } from "apis/receiptApi";
import { Loading } from "components";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import { DebtResponseType } from "constants/types/debt";
import { BulkErrorMessage } from "constants/types/receipt";
import { FormikProvider, useFormik } from "formik";
import { FC, useState } from "react";
import { useNotification } from "utils/hooks";
import BulkReceiptAmountsStep from "./components/BulkReceiptAmountsStep/BulkReceiptAmountsStep";
import BulkReceiptPaymentStep from "./components/BulkReceiptPaymentStep/BulkReceiptPaymentStep";
import {
  BulkReceiptItemsFormValues,
  BulkReceiptPaymentFormValues,
  bulkItemsValidationSchema,
  bulkPaymentValidationSchema,
  initialBulkItemsValues,
  initialBulkPaymentValues,
  toBulkCreateBody,
  validateBulkGroupSums,
} from "./config";

type WizardStep = 1 | 2;

const STEP_LABEL: Record<WizardStep, string> = {
  1: "Nhập số tiền cần thu",
  2: "Chọn phương thức thanh toán",
};

interface BulkReceiptCreateFormProps {
  /** Snapshot công nợ đã chọn tại thời điểm mở modal — không đổi theo bảng phía sau. */
  debts: DebtResponseType[];
  onClose?: () => void;
  mutate?: () => void;
  /** Gọi khi tạo phiếu thành công, để nơi gọi xóa lựa chọn hiện tại. */
  onSuccess?: () => void;
}

const toErrorList = (message: BulkErrorMessage | undefined): string[] => {
  if (!message) return ["Tạo nhiều phiếu thu thất bại"];
  return Array.isArray(message) ? message : [message];
};

/**
 * Modal tạo nhiều phiếu thu theo 2 bước:
 * 1) Nhập số tiền cần thu cho từng công nợ đã chọn (có thể thêm nhiều đợt
 *    thu cho cùng một công nợ).
 * 2) Chọn phương thức thanh toán MỘT LẦN, áp dụng chung cho mọi phiếu thu ở
 *    bước 1, rồi gọi API tạo hàng loạt.
 *
 * Modal tự vẽ footer (Hủy/Tiếp theo, Quay lại/Tạo phiếu thu) nên được mở với
 * `hideFooter: true` — xem WebLayout.tsx.
 */
const BulkReceiptCreateForm: FC<BulkReceiptCreateFormProps> = ({
  debts,
  onClose,
  mutate,
  onSuccess,
}) => {
  const { notification } = useNotification();
  const [step, setStep] = useState<WizardStep>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<string[] | null>(null);

  const itemsFormik = useFormik<BulkReceiptItemsFormValues>({
    initialValues: initialBulkItemsValues(debts),
    validationSchema: bulkItemsValidationSchema,
    validate: validateBulkGroupSums,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: () => setStep(2),
  });

  const paymentFormik = useFormik<BulkReceiptPaymentFormValues>({
    initialValues: initialBulkPaymentValues(),
    validationSchema: bulkPaymentValidationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (payment) => {
      setIsSubmitting(true);
      setSubmitErrors(null);
      try {
        const res = await receiptApi.createMany(
          toBulkCreateBody(itemsFormik.values.items, payment),
        );
        if (res.data.success) {
          notification.success({
            title: `Đã tạo thành công ${itemsFormik.values.items.length} phiếu thu`,
          });
          onClose?.();
          mutate?.();
          onSuccess?.();
        }
      } catch (error: any) {
        const errors = toErrorList(error?.error?.message);
        setSubmitErrors(errors);
        notification.error({ title: "Tạo nhiều phiếu thu thất bại" });
      }
      setIsSubmitting(false);
    },
  });

  const activeFormik = step === 1 ? itemsFormik : paymentFormik;

  return (
    <Loading variant="overlay" visible={isSubmitting}>
      <Paper>
        <Stack gap="md">
          <Stack gap={4}>
            <Group gap={8} align="center">
              <CustomBadge label={`Bước ${step}/2`} color="blue" size="lg" />
              <Text size="md" fw={700} c="dark.7">
                {STEP_LABEL[step]}
              </Text>
            </Group>
            <Divider />
          </Stack>

          {submitErrors && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={18} />}
              title="Tạo nhiều phiếu thu thất bại"
            >
              <List size="sm">
                {submitErrors.map((message, i) => (
                  <List.Item key={i}>{message}</List.Item>
                ))}
              </List>
            </Alert>
          )}

          <FormikProvider value={activeFormik}>
            <form onSubmit={activeFormik.handleSubmit}>
              {step === 1 ? (
                <BulkReceiptAmountsStep formik={itemsFormik} />
              ) : (
                <BulkReceiptPaymentStep
                  formik={paymentFormik}
                  receiptCount={itemsFormik.values.items.length}
                />
              )}
            </form>
          </FormikProvider>

          <Group justify="space-between">
            {step === 1 ? (
              <Button variant="default" onClick={onClose} disabled={isSubmitting}>
                Hủy
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={() => setStep(1)}
                disabled={isSubmitting}
              >
                Quay lại
              </Button>
            )}
            <Button
              onClick={() => activeFormik.submitForm()}
              loading={isSubmitting}
            >
              {step === 1 ? "Tiếp theo" : "Tạo phiếu thu"}
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Loading>
  );
};

export default BulkReceiptCreateForm;
